var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, {
  enumerable: true,
  configurable: true,
  writable: true,
  value
}) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {})) if (__hasOwnProp.call(b, prop)) __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols) for (var prop of __getOwnPropSymbols(b)) {
    if (__propIsEnum.call(b, prop)) __defNormalProp(a, prop, b[prop]);
  }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source) if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0) target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols) for (var prop of __getOwnPropSymbols(source)) {
    if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop)) target[prop] = source[prop];
  }
  return target;
};
import { treeMenuContext } from "@whitespace/components/src/contexts";
import { useSet } from "@whitespace/components/src/hooks";
import clsx from "clsx";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { filterAttributes } from "../utils";
import * as defaultStyles from "./TreeMenu.module.css";
import TreeMenuList from "./TreeMenuList";
function encodePath(path) {
  return JSON.stringify(path);
}
function decodePath(path) {
  return JSON.parse(path);
}
function documentPositionComparator(a, b) {
  if (a === b) {
    return 0;
  }
  var position = a.compareDocumentPosition(b);
  if (position & Node.DOCUMENT_POSITION_FOLLOWING || position & Node.DOCUMENT_POSITION_CONTAINED_BY) {
    return -1;
  } else if (position & Node.DOCUMENT_POSITION_PRECEDING || position & Node.DOCUMENT_POSITION_CONTAINS) {
    return 1;
  } else {
    return 0;
  }
}
export default function TreeMenu(_a) {
  var _b = _a,
    {
      styles = defaultStyles,
      className,
      items,
      location
    } = _b,
    restProps = __objRest(_b, ["styles", "className", "items", "location"]);
  const [expandedItems, {
    toggle: toggleExpandedItem,
    has: isItemExpanded,
    add: expandItem,
    delete: collapseItem
  }] = useSet([], (a, b) => encodePath(a) === encodePath(b));
  const itemsRef = useRef({});
  const focusDummyRef = useRef();
  const [encodedFocusedPath, setEncodedFocusedPath] = useState(encodePath([0]));
  const focusedPath = decodePath(encodedFocusedPath);
  const setFocusedPath = useCallback(path => {
    setEncodedFocusedPath(encodePath(path.map((index, pos) => index >= 0 ? index : getItemByPath(path.slice(0, pos + 1)).children.length + index)));
  }, []);
  const isFocusedPath = useCallback(path => {
    return encodePath(path) === encodePath(focusedPath);
  }, [focusedPath]);
  const getAllItemPaths = useCallback(function (items2) {
    let parentPath = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
    return items2.reduce((acc, item, index) => {
      const path = [...parentPath, index];
      acc.push({
        path,
        url: item.url
      });
      if (item.children) {
        acc.push(...getAllItemPaths(item.children, path));
      }
      return acc;
    }, []);
  }, [items]);
  const getItemByPath = useCallback(path => {
    return path.reduce((item, index) => {
      var _a2, _b2;
      return (_b2 = item == null ? void 0 : item.children) == null ? void 0 : _b2[index >= 0 ? index : ((_a2 = item == null ? void 0 : item.children) == null ? void 0 : _a2.length) + index];
    }, {
      children: items
    });
  }, [items]);
  const isCurrentItem = useCallback(item => {
    return item.url && item.url === location.pathname;
  }, [location.pathname]);
  const itemPaths = useMemo(() => getAllItemPaths(items), [items]);
  const findItemPath = useCallback(condition => {
    var _a2;
    return ((_a2 = itemPaths.find(condition)) == null ? void 0 : _a2.path) || null;
  }, [items]);
  const currentItemPath = useMemo(() => findItemPath(isCurrentItem), [isCurrentItem, findItemPath]);
  useEffect(() => {
    expandedItems.forEach(item => collapseItem(item));
    (currentItemPath || []).reduce((path, index) => {
      path = [...path, index];
      expandItem(path);
      return path;
    }, []);
  }, [encodePath(currentItemPath)]);
  const registerItemElement = useCallback((path, element) => {
    if (element == null) {
      let prevElement = itemsRef.current[encodePath(path)];
      if (prevElement === document.activeElement) {
        focusDummyRef.current.focus();
      }
      delete itemsRef.current[encodePath(path)];
    } else {
      itemsRef.current[encodePath(path)] = element;
      updateFocus();
    }
  }, []);
  const ref = useRef();
  const updateFocus = useCallback(() => {
    let element = itemsRef.current[encodedFocusedPath];
    if (element && document.activeElement !== element && ref.current && ref.current.contains(document.activeElement)) {
      element.focus();
    }
  }, [encodedFocusedPath]);
  useEffect(() => {
    updateFocus();
  }, [updateFocus]);
  const handleDownKey = useCallback(() => {
    let focusedElement = itemsRef.current[encodedFocusedPath];
    let focusableElements = Object.values(itemsRef.current);
    focusableElements.sort(documentPositionComparator);
    let index = focusableElements.indexOf(focusedElement) + 1;
    let nextElement = focusableElements[index % focusableElements.length];
    let key = Object.keys(itemsRef.current).find(key2 => itemsRef.current[key2] === nextElement);
    setEncodedFocusedPath(key);
  }, [encodedFocusedPath]);
  const handleUpKey = useCallback(() => {
    let focusedElement = itemsRef.current[encodedFocusedPath];
    let focusableElements = Object.values(itemsRef.current);
    focusableElements.sort(documentPositionComparator);
    let index = focusableElements.indexOf(focusedElement) - 1;
    let prevElement = focusableElements[(focusableElements.length + index) % focusableElements.length];
    let key = Object.keys(itemsRef.current).find(key2 => itemsRef.current[key2] === prevElement);
    setEncodedFocusedPath(key);
  }, [encodedFocusedPath]);
  const handleRightKey = useCallback(() => {
    if (!isItemExpanded(focusedPath)) {
      expandItem(focusedPath);
    } else {
      let firstChildPath = [...focusedPath, 0];
      let firstChildItem = getItemByPath(firstChildPath);
      if (firstChildItem) {
        setFocusedPath(firstChildPath);
      }
    }
  }, [encodedFocusedPath, focusedPath]);
  const handleLeftKey = useCallback(() => {
    if (isItemExpanded(focusedPath)) {
      collapseItem(focusedPath);
    } else {
      let parentPath = focusedPath.slice(0, -1);
      if (parentPath.length > 0) {
        setFocusedPath(parentPath);
      }
    }
  }, [focusedPath]);
  const handleSpaceKey = useCallback(() => {
    toggleExpandedItem(focusedPath);
  }, [focusedPath]);
  const handleEndKey = useCallback(() => {
    let focusableElements = Object.values(itemsRef.current);
    focusableElements.sort(documentPositionComparator);
    let lastElement = focusableElements[focusableElements.length - 1];
    let key = Object.keys(itemsRef.current).find(key2 => itemsRef.current[key2] === lastElement);
    setEncodedFocusedPath(key);
  }, [encodedFocusedPath]);
  const handleHomeKey = useCallback(() => {
    let focusableElements = Object.values(itemsRef.current);
    focusableElements.sort(documentPositionComparator);
    let firstElement = focusableElements[0];
    let key = Object.keys(itemsRef.current).find(key2 => itemsRef.current[key2] === firstElement);
    setEncodedFocusedPath(key);
  }, [encodedFocusedPath]);
  const treeMenuContextValue = {
    expandedItems,
    isCurrentItem,
    isFocusedPath,
    isItemExpanded,
    registerItemElement,
    setFocusedPath,
    styles,
    toggleExpandedItem
  };
  let attributes = filterAttributes(restProps);
  return /* @__PURE__ */React.createElement(treeMenuContext.Provider, {
    value: treeMenuContextValue
  }, /* @__PURE__ */React.createElement("nav", __spreadProps(__spreadValues({
    className: clsx(className, styles.component)
  }, attributes), {
    ref,
    onFocus: () => {
      updateFocus();
    },
    onKeyDown: event => {
      switch (event.key) {
        case "Down":
        case "ArrowDown":
          event.preventDefault();
          event.stopPropagation();
          return handleDownKey(event);
        case "Up":
        case "ArrowUp":
          event.preventDefault();
          event.stopPropagation();
          return handleUpKey(event);
        case "Right":
        case "ArrowRight":
          event.preventDefault();
          event.stopPropagation();
          return handleRightKey(event);
        case "Left":
        case "ArrowLeft":
          event.preventDefault();
          event.stopPropagation();
          return handleLeftKey(event);
        case "End":
          event.preventDefault();
          event.stopPropagation();
          return handleEndKey(event);
        case "Home":
          event.preventDefault();
          event.stopPropagation();
          return handleHomeKey(event);
        case " ":
          event.preventDefault();
          event.stopPropagation();
          return handleSpaceKey(event);
      }
    }
  }), /* @__PURE__ */React.createElement("span", {
    tabIndex: -1,
    ref: focusDummyRef
  }), /* @__PURE__ */React.createElement(TreeMenuList, {
    items
  })));
}